/**
 * 生成页 - UX 优化版
 *
 * 优化点：
 * 1. 添加生成进度反馈（预估时间 + 步骤指示）
 * 2. 使用下拉选择替代按钮网格，节省空间
 * 3. 添加骨架屏加载状态
 * 4. 将上传功能折叠到"高级选项"
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Loader2,
  ChevronRight,
  ChevronDown,
  Settings2,
  CheckCircle2,
  Circle,
  Upload,
} from 'lucide-react';
import { useAppStore } from '../store/appStore';
import { generateOutline } from '../lib/api';
import type { Genre, WritingStyle } from '../../../shared/types';

const genres: { value: Genre; label: string; emoji: string }[] = [
  { value: 'urban', label: '都市', emoji: '🏙️' },
  { value: 'xuanhuan', label: '玄幻', emoji: '⚡' },
  { value: 'xianxia', label: '仙侠', emoji: '🗡️' },
  { value: 'history', label: '历史', emoji: '📜' },
  { value: 'scifi', label: '科幻', emoji: '🚀' },
  { value: 'romance', label: '言情', emoji: '💕' },
  { value: 'suspense', label: '悬疑', emoji: '🔍' },
  { value: 'wuxia', label: '武侠', emoji: '⚔️' },
];

const styles: { value: WritingStyle; label: string; desc: string }[] = [
  { value: 'fast-paced', label: '快节奏爽文', desc: '打脸升级，持续高潮' },
  { value: 'commercial', label: '商业黄金', desc: '平衡爽感和深度' },
  { value: 'literary', label: '文青慢热', desc: '细腻情感，深度思考' },
  { value: 'innovation', label: '创新反套路', desc: '颠覆传统，新颖独特' },
];

const wordCountOptions = [
  { value: 100000, label: '10 万字' },
  { value: 300000, label: '30 万字' },
  { value: 500000, label: '50 万字（推荐）' },
  { value: 1000000, label: '100 万字' },
];

// 生成步骤（用于进度显示）
const GENERATION_STEPS = [
  { id: 1, text: '分析类型特征', duration: 5 },
  { id: 2, text: '生成核心设定', duration: 10 },
  { id: 3, text: '设计人物弧光', duration: 8 },
  { id: 4, text: '构建三幕结构', duration: 15 },
  { id: 5, text: '植入黄金爽点', duration: 10 },
  { id: 6, text: '评估大纲质量', duration: 5 },
];

export default function Generate() {
  const navigate = useNavigate();
  const {
    genre,
    style,
    targetWordCount,
    customPrompt,
    analyzedStructure,
    setGenre,
    setStyle,
    setTargetWordCount,
    setCustomPrompt,
    setGeneratedOutline,
    setIsGenerating,
    setError,
    isGenerating,
  } = useAppStore();

  const [localCustomPrompt, setLocalCustomPrompt] = useState(customPrompt);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  const canGenerate = genre !== null;

  // 模拟进度更新
  const simulateProgress = () => {
    let step = 0;
    const totalDuration = GENERATION_STEPS.reduce((sum, s) => sum + s.duration, 0);

    const interval = setInterval(() => {
      if (step >= GENERATION_STEPS.length) {
        clearInterval(interval);
        return;
      }

      setCurrentStep(step + 1);

      // 计算累计进度
      const elapsed = GENERATION_STEPS.slice(0, step + 1).reduce((sum, s) => sum + s.duration, 0);
      setProgress(Math.round((elapsed / totalDuration) * 100));

      step++;
    }, 5000); // 每5秒切换一步

    return interval;
  };

  const handleGenerate = async () => {
    if (!genre) return;

    setIsGenerating(true);
    setError(null);
    setCurrentStep(0);
    setProgress(0);

    const progressInterval = simulateProgress();

    try {
      const result = await generateOutline({
        genre,
        style,
        targetWordCount,
        referenceStructure: analyzedStructure || undefined,
        customPrompt: localCustomPrompt || undefined,
      });

      // 传递 demoMode 到 store
      setGeneratedOutline(result.outline, result.variations, result.demoMode);
      navigate(`/result/${result.outline.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : '生成失败');
    } finally {
      clearInterval(progressInterval);
      setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <AnimatePresence mode="wait">
        {isGenerating ? (
          // 生成进度界面
          <motion.div
            key="progress"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center py-12"
          >
            {/* 动画图标 */}
            <div className="w-20 h-20 mx-auto mb-8 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full animate-pulse opacity-20" />
              <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-primary-500 animate-bounce" />
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-2">正在生成大纲...</h2>
            <p className="text-gray-500 mb-8">预计需要 30-60 秒</p>

            {/* 进度条 */}
            <div className="max-w-md mx-auto mb-8">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-primary-500 to-accent-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <p className="text-sm text-gray-400 mt-2">{progress}% 完成</p>
            </div>

            {/* 步骤列表 */}
            <div className="max-w-sm mx-auto space-y-3">
              {GENERATION_STEPS.map((step, index) => (
                <div
                  key={step.id}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    currentStep > index
                      ? 'bg-green-50 text-green-700'
                      : currentStep === index
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-400'
                  }`}
                >
                  {currentStep > index ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : currentStep === index ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Circle className="w-5 h-5" />
                  )}
                  <span className="text-sm">{step.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        ) : (
          // 配置界面
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* 标题 */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-2">生成爆款大纲</h1>
              <p className="text-gray-500">选择类型，AI 帮你搞定剩下的</p>
            </div>

            {/* 参考文本提示（如果有） */}
            {analyzedStructure && (
              <div className="bg-green-50 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <div className="text-sm text-green-700">
                  <p className="font-medium">已加载参考文本结构</p>
                  <p className="text-green-600">AI 将参考分析结果优化生成</p>
                </div>
              </div>
            )}

            {/* 类型选择 - 使用下拉菜单节省空间 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                小说类型 <span className="text-red-500">*</span>
              </label>
              <select
                value={genre || ''}
                onChange={(e) => setGenre(e.target.value as Genre)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all cursor-pointer bg-white"
              >
                <option value="" disabled>选择类型...</option>
                {genres.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.emoji} {g.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 风格选择 - 紧凑卡片 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                写作风格
              </label>
              <div className="grid grid-cols-2 gap-2">
                {styles.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => setStyle(s.value)}
                    className={`px-4 py-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                      style === s.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-primary-300 bg-white'
                    }`}
                  >
                    <div className="font-medium text-sm">{s.label}</div>
                    <div className="text-xs text-gray-400">{s.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* 字数选择 - 紧凑按钮组 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                目标字数
              </label>
              <div className="flex flex-wrap gap-2">
                {wordCountOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTargetWordCount(opt.value)}
                    className={`px-4 py-2 rounded-lg border-2 text-sm transition-all cursor-pointer ${
                      targetWordCount === opt.value
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-primary-300 bg-white'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 高级选项 - 折叠区 */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <Settings2 className="w-4 h-4" />
                  高级选项
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showAdvanced && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 space-y-4 border-t border-gray-200">
                      {/* 自定义提示 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          额外要求（可选）
                        </label>
                        <textarea
                          value={localCustomPrompt}
                          onChange={(e) => setLocalCustomPrompt(e.target.value)}
                          onBlur={() => setCustomPrompt(localCustomPrompt)}
                          placeholder="描述特殊要求，如：主角是程序员穿越、有系统金手指..."
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none min-h-[80px] resize-none"
                        />
                      </div>

                      {/* 上传参考 - 移到高级选项 */}
                      <div>
                        <Link
                          to="/upload"
                          className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                        >
                          <Upload className="w-4 h-4" />
                          上传参考文本（可选）
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* 生成按钮 */}
            <button
              onClick={handleGenerate}
              disabled={!canGenerate}
              className={`w-full flex items-center justify-center gap-3 py-4 rounded-xl font-semibold text-lg transition-all cursor-pointer ${
                canGenerate
                  ? 'bg-gradient-to-r from-primary-600 to-accent-500 hover:from-primary-700 hover:to-accent-600 text-white shadow-lg shadow-primary-500/25'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Sparkles className="w-5 h-5" />
              一键生成爆款大纲
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* 提示 */}
            <p className="text-center text-sm text-gray-400">
              生成时间约 30-60 秒，包含完整设定和章节规划
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
